<template>
  <dp-container v-if="loading">
    <dp-loading />
  </dp-container>
  <dp-container v-else v-testid="'tags-ready'" class="d-flex flex-column">
    <!-- Back Link -->
    <b-row v-if="isAdmin" class="mt-3 mb-4">
      <b-col cols="6">
        <b-link :to="{ name: 'AdminRedirect' }"> {{ '&#8592; Back' }} </b-link>
      </b-col>
    </b-row>
    <!-- NASN cohort intro (adoption journey) -->
    <section v-if="showNasnCohortUi" class="nasn-intro">
      <div class="nasn-intro__title-group">
        <h1 v-testid="'tags-nasn-intro-heading'" class="nasn-intro__title">{{ $t('tags.nasn.title') }}</h1>
        <p class="nasn-intro__subtitle">{{ $t('tags.nasn.subtitle', { petName: nasnPetName }) }}</p>
      </div>
      <hr class="nasn-intro__divider" />
    </section>
    <!-- Product Type Navbar -->
    <ProductTypeNavbar
      v-if="
        !isAdmin && !isLicensingFlow && city && (city.showCollarsLeashesProducts || city.showPetEssentialsProducts) && !showNasnCohortUi
      "
    />
    <!-- Pet Header & Marketing Banner (replaced by the NASN intro for the cohort) -->
    <section v-if="!showNasnCohortUi">
      <marketing-banner
        :variant="marketingBannerVariant"
        :city-name="city.name"
        :city-logo="cityLogo"
        :pet="requiredPet ? requiredPet : null"
        :is-admin="isAdmin"
      />
    </section>
    <!-- STORE BODY -->
    <section>
      <b-row class="d-flex flex-column align-items-start" no-gutters>
        <!-- Product Groups & Product Group Styles -->
        <b-col class="d-flex flex-column">
          <!-- Primary Marketing Collections  -->
          <template v-if="!isLicensingFlow">
            <MarketingContent
              v-for="(content, index) in primaryMarketingCollection"
              :key="`p-marketing-collection-content-${index}`"
              :content="getMarketingContent(content)"
              :content-type="getMarketingContentType(content)"
              :style="{ order: content ? content.position : 0 }"
            />
          </template>
          <section
            class="mx-2 mx-lg-auto"
            :style="{ order: 0 }"
            :class="{ 'pb-4': (!isAdmin && !city.marketingAdminCopy) || (isAdmin && municipalGroup) }"
          >
            <!-- Marketing -->
            <div
              v-if="marketingInfo.info"
              class="marketing-info d-flex align-items-center justify-content-start flex-gap-1 ml-1 ml-lg-0 mb-4"
            >
              <b-img v-if="marketingInfo.img" :src="getImage(marketingInfo.img)" :alt="`${marketingInfo.info}`" width="80" fluid />
              <!-- eslint-disable-next-line vue/no-v-html (Make sure it's always hard-coded) -->
              <div class="flex-grow-1" v-html="marketingInfo.info" />
            </div>
            <!-- Donation Info and Municipal Tag Row -->
            <!-- Agnostic City -->
            <div
              v-if="city.noLicensing && !isPartnerOrShelterAdmin"
              class="d-flex flex-column-reverse flex-lg-row flex-gap-1 align-items-center"
            >
              <b-row class="municipal-tag flex-1 d-flex align-items-center" no-gutters>
                <!-- NASN cohort: "Free Tag is Ready" card -->
                <div
                  v-if="showNasnFreeTagReady && city.freePetTagProductGroup"
                  class="nasn-free-tag cursor-pointer"
                  @click="redirectToStyleId(city.freePetTagProductGroup.productStyles[0].id)"
                >
                  <div class="nasn-free-tag__image-box">
                    <b-img :src="getImage(city.freePetTagProductGroup.image.original)" alt="" class="nasn-free-tag__img" />
                  </div>
                  <div class="nasn-free-tag__text">
                    <span class="nasn-free-tag__ready">{{ $t('tags.nasn.freeTagReady', { petName: nasnPetName }) }}</span>
                    <span v-testid="'tags-nasn-free-tag-action'" class="nasn-free-tag__cta">{{ $t('tags.nasn.checkItOut') }}</span>
                  </div>
                </div>
                <b-row
                  v-if="!showNasnFreeTagReady && city.freePetTagProductGroup"
                  class="municipal-tag flex-1 d-flex align-items-center"
                  no-gutters
                >
                  <b-col
                    class="mr-3 cursor-pointer"
                    cols="auto"
                    @click="redirectToStyleId(city.freePetTagProductGroup.productStyles[0].id)"
                  >
                    <b-img :src="getImage(city.freePetTagProductGroup.image.original)" alt="" center class="py-1" fluid />
                  </b-col>
                  <b-col class="cursor-pointer" @click="redirectToStyleId(city.freePetTagProductGroup.productStyles[0].id)">
                    <h2 class="font-weight-bold text-decoration-underline h3">{{ $t('tags.freeTag.text.default') }}</h2>
                    <h3 class="mt-n2 dp-blue">{{ $t(`tags.freeTag.subtext`) }}</h3>
                  </b-col>
                </b-row>
              </b-row>
            </div>
            <div
              v-if="!city.noLicensing"
              class="d-flex flex-column-reverse flex-lg-row flex-gap-1 align-items-center justify-content-lg-center"
            >
              <!-- Donation Info -->
              <div v-if="!isAdmin && (!city.replaceTagStoreCTAOption || municipalGroup)" class="pb-2">
                <h2
                  v-if="!city.replaceTagStoreCTAOption"
                  class="d-flex align-items-center justify-content-center font-weight-bold ml-2 ml-md-1"
                >
                  <b-img
                    :src="require('@/assets/images/icon_handheart.png')"
                    :srcset="`${require('@/assets/images/icon_handheart@3x.png')} 3x, ${require('@/assets/images/icon_handheart@2x.png')} 2x, ${require('@/assets/images/icon_handheart.png')} 1x`"
                    fluid
                    alt=""
                  />
                  <!-- eslint-disable-next-line vue/no-v-html (Make sure it's always hard-coded) -->
                  <span class="ml-3" v-html="$t('tags.title', { cityName: city.safeAndHappyFund || 'the Safe&Happy Fund' })" />
                </h2>
                <div v-else>
                  <b-row v-if="municipalGroup && !city.viewBestSellersCtaHidden" class="municipal-tag d-flex align-items-center" no-gutters>
                    <b-col cols="auto" class="mr-3 cursor-pointer" @click="scrollToHash('best-sellers')">
                      <b-img :src="require('@/assets/images/product-tag-hrt-thumbnail.png')" alt="" center class="py-1" fluid />
                    </b-col>
                    <b-col class="cursor-pointer" @click="scrollToHash('best-sellers')">
                      <h3 class="font-weight-bold" v-html="defaultOrReplacementCTAPromoText"></h3>
                      <h3 class="text-decoration-underline mt-n2 dp-blue">{{ $t(`tags.titleSubtext`) }}</h3>
                    </b-col>
                  </b-row>
                </div>
              </div>
              <!-- NASN cohort: "Free Tag is Ready" card (Figma: bordered 80x80 image box + text sibling) -->
              <div
                v-if="showNasnFreeTagReady && city.freePetTagProductGroup && !isLicensingFlow && !isPartnerOrShelterAdmin"
                class="nasn-free-tag cursor-pointer"
                @click="redirectToStyleId(city.freePetTagProductGroup.productStyles[0].id)"
              >
                <div class="nasn-free-tag__image-box">
                  <b-img :src="getImage(city.freePetTagProductGroup.image.original)" alt="" class="nasn-free-tag__img" />
                </div>
                <div class="nasn-free-tag__text">
                  <span class="nasn-free-tag__ready">{{ $t('tags.nasn.freeTagReady', { petName: nasnPetName }) }}</span>
                  <span v-testid="'tags-nasn-free-tag-action'" class="nasn-free-tag__cta">{{ $t('tags.nasn.checkItOut') }}</span>
                </div>
              </div>
              <b-row
                v-if="!showNasnFreeTagReady && city.freePetTagProductGroup && !isLicensingFlow && !isPartnerOrShelterAdmin"
                class="municipal-tag d-flex align-items-center"
                no-gutters
              >
                <b-col cols="auto" class="mr-3 cursor-pointer" @click="redirectToStyleId(city.freePetTagProductGroup.productStyles[0].id)">
                  <b-img :src="getImage(city.freePetTagProductGroup.image.original)" alt="" center class="py-1" fluid />
                </b-col>
                <b-col class="cursor-pointer" @click="redirectToStyleId(city.freePetTagProductGroup.productStyles[0].id)">
                  <h2 class="font-weight-bold text-decoration-underline h3">{{ $t('tags.freeTag.text.default') }}</h2>
                  <h3 class="mt-n2 dp-blue">{{ $t(`tags.freeTag.subtext`) }}</h3>
                </b-col>
              </b-row>
              <!-- Municipal Tag -->
              <b-row v-if="municipalGroup && isLicensingFlow" class="municipal-tag d-flex align-items-center" no-gutters>
                <b-col cols="auto" class="mr-3 cursor-pointer" @click="redirectToStyleId(municipalGroup.productStyles[0].id)">
                  <b-img :src="getImage(municipalGroup.image.original)" alt="" center class="py-1" fluid />
                </b-col>
                <b-col class="cursor-pointer" @click="redirectToStyleId(municipalGroup.productStyles[0].id)">
                  <h3 class="font-weight-bold" v-html="defaultOrCustomCTAOptionText"></h3>
                  <h3 v-if="isAdmin || !city.replaceTagStoreCTAOption" class="font-weight-bold dp-blue mt-n2">
                    ${{ Number(municipalGroup.price).toFixed(2) }}
                  </h3>
                  <h3 v-else class="text-decoration-underline mt-n2 dp-blue">{{ $t(`tags.municipalTag.subtext`, [city.name]) }}</h3>
                </b-col>
                <b-col v-if="isAdmin && canSkipLicensing">
                  <h4 class="text-dark text-right font-weight-bold">
                    {{ $t(`petHeader.skipLicensing.text.${isAdmin ? 'admin' : 'default'}`, { petName: requiredPet.name }) }}
                    <br />
                    <b-link href="javascript:void(0)" @click="orderNoTag">{{ $t('petHeader.skipLicensing.linkText') }}</b-link>
                  </h4>
                </b-col>
              </b-row>
            </div>
          </section>
          <!-- Search Tag -->
          <search
            :key="`s_${searchKey}`"
            v-model="search"
            :style="{ order: 1 }"
            class="search-bar mx-1 mb-lg-3 ml-lg-0"
            :pet-id="petId"
            :groups="filteredGroups"
            @scrollToHash="scrollToHash"
            @search-term-changed="(searchTerm) => searchTermChanged(searchTerm)"
          />
          <!-- Designer Tag Collections -->
          <product-group
            id="collections"
            ref="product-groups"
            :style="{ order: 2 }"
            :groups="filteredGroups"
            :pet-id="petId"
            :show-all-tags="showAllTags"
          >
            <template #marketing-content-collection>
              <!-- Secondary Marketing Collections  -->
              <MarketingContent
                v-for="(content, index) in secondaryMarketingCollection"
                v-show="!search"
                :key="`s-marketing-collection-content-${index}`"
                :content="getMarketingContent(content)"
                :content-type="getMarketingContentType(content)"
                :style="{ order: content ? content.position : 0 }"
              />
            </template>
          </product-group>
          <!-- Skip Tag Selection Process (Licensing Flow) -->
          <section v-if="isAdmin && canSkipLicensing">
            <div class="fixed-bottom py-2 py-lg-4 border-top bg-info">
              <h3 class="d-none d-lg-block text-center text-dark px-auto font-weight-bold">
                {{ $t(`petHeader.skipLicensing.text.admin`) }}
                <b-link href="javascript:void(0)" @click="orderNoTag">{{ $t('petHeader.skipLicensing.linkText') }}</b-link>
              </h3>
              <h4 class="d-block d-lg-none text-center text-dark px-auto font-weight-bold">
                {{ $t(`petHeader.skipLicensing.text.admin`) }}
                <b-link href="javascript:void(0)" @click="orderNoTag">{{ $t('petHeader.skipLicensing.linkText') }}</b-link>
              </h4>
            </div>
          </section>
        </b-col>
      </b-row>
    </section>
    <section v-show="filteredGroups.length === 0" id="search-empty" class="mt-5">
      <b-card overlay :img-src="require('@/assets/images/404.jpg')" img-fluid img-alt="" class="border-0">
        <b-card-title class="d-none d-lg-block display-4 font-weight-bold mt-4">{{ $t('dp.productNotFound.notFound') }}</b-card-title>
        <b-card-title class="d-block d-lg-none h2 font-weight-bold mt-3">{{ $t('dp.productNotFound.notFound') }}</b-card-title>
        <b-card-body
          v-if="productGroups.length > 0 && productGroups.filter((group) => !group || !group.municipalGroup).length > 0"
          class="mt-lg-5"
        >
          <b-button variant="primary" class="px-5" @click="clearSearch">{{ $t('dp.productNotFound.buttons.viewAll') }}</b-button>
        </b-card-body>
      </b-card>
    </section>
  </dp-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import getImage from '@/mixins/getImage';
import { DpContainer, DpLoading } from '@docupet/component-library';
import ProductGroup from '@/components/ProductGroup';
import Search from '@/components/Search';
import MarketingBanner from '@/components/MarketingBanner';
import ProductTypeNavbar from '@/components/ProductTypeNavbar';
import { NotificationMixins } from '@/mixins/notification';
import MarketingContent from '@/components/MarketingContent';

export default {
  name: 'Tags',
  components: {
    MarketingContent,
    ProductTypeNavbar,
    MarketingBanner,
    DpContainer,
    DpLoading,
    ProductGroup,
    Search,
  },
  mixins: [NotificationMixins, getImage],
  props: {
    petId: {
      type: [String, Number],
      default: '',
      required: false,
    },
    showAllTags: {
      type: Boolean,
      default: true,
      required: false,
    },
  },
  data() {
    return {
      search: '',
      searchKey: 0,
    };
  },
  metaTag() {
    return this.$router.currentRoute.meta;
  },
  computed: {
    ...mapState({
      cityId: (state) => state.cityId,
      orderId: (state) => state.orderId,
      city: (state) => state.user.city,
      order: (state) => state.order.order,
      loading: (state) => state.tagProduct?.loading || state.order?.itemLoading,
      productGroups: (state) => state.tagProduct?.groups || [],
      isAdmin: (state) => state.user.user.id && state.user.user.__typename == 'Administrator',
      isInternalAdmin: (state) =>
        state.user.user.id && state.user.user.__typename === 'Administrator' && state.user.user.roles?.includes('ROLE_INTERNAL'),
      isPartnerOrShelterAdmin: (state) =>
        state.user.user.id &&
        state.user.user.__typename === 'Administrator' &&
        ['ROLE_PARTNER_ADMIN', 'ROLE_SHELTER_ADMIN'].some((role) => state.user.user.roles?.includes(role)),
      primaryMarketingCollection: (state) => state.user?.city?.tagsLandingPagePrimaryMarketingCollection?.marketingContentCollectionItems,
      secondaryMarketingCollection: (state) =>
        state.user?.city?.tagsLandingPageSecondaryMarketingCollection?.marketingContentCollectionItems,
    }),
    ...mapGetters({
      somePetsWithLicense: 'order/somePetsWithLicense',
      getRequiredPetByPetId: 'order/getRequiredPetByPetId',
      freePetTagGroup: 'user/getCityFreeTagProductGroup',
      isNasnCohort: 'isNasnCohort',
    }),
    requiredPet() {
      return this.getRequiredPetByPetId(this.petId, !(this.isAdmin && this.$router?.currentRoute?.query?.single));
    },
    // NASN cohort: the tag store is per-pet (pet id is in the route), so the copy
    // reflects the single required pet, not the whole litter.
    nasnPetName() {
      return this.requiredPet?.name || '';
    },
    // NASN cohort UI is per-pet. Without a pet id in the URL there is no required
    // pet (and no pet name), so fall back to the standard (non-NASN) store screen.
    showNasnCohortUi() {
      return this.isNasnCohort && !!this.requiredPet;
    },
    // NASN cohort: show the "Free Tag is Ready" treatment, but hide it when the
    // pet already owns a HomeSafe tag (per the Figma note).
    showNasnFreeTagReady() {
      return this.showNasnCohortUi && !this.requiredPet?.hasPreviousTag && !this.requiredPet?.hasTag;
    },
    canSkipLicensing() {
      return this.requiredPet?.licensing && (this.requiredPet?.hasPreviousTag || this.requiredPet?.hasPreviousMunicipalTag);
    },
    isLicensingFlow() {
      return this.requiredPet?.licensing && !this.requiredPet?.hasTag;
    },
    municipalTag() {
      return this.productGroups?.find?.((group) => group?.municipalGroup);
    },
    municipalGroup() {
      // Municipal Groups are only displayed when licensing and only if pet doesn't have municipal tag before or in the basket
      if (!this.isInternalAdmin && (!this.requiredPet?.licensing || (this.requiredPet?.licensing && this.requiredPet?.hasMunicipalTag)))
        return;

      return this.productGroups?.find?.((group) => group?.municipalGroup);
    },
    filteredGroups() {
      try {
        let groups = [];
        this.productGroups.forEach((group) => {
          if (group?.municipalGroup) return;
          /**
           * - SEARCHING LOGIC -
           * Search if term matches tag name or description
           * YES -> Show collection with matching tags
           * NO  -> Search if collection name matches term
           *        YES -> Show collection with all tags
           *        NO  -> No Products Found!
           **/
          const matchingTags = group.productStyles.filter(
            (s) =>
              s.name.toLowerCase().includes(this.search.toLowerCase()) ||
              (s.keywords && s.keywords.toLowerCase().includes(this.search.toLowerCase())) ||
              s.description?.toLowerCase().includes(this.search.toLowerCase())
          );
          if (matchingTags.length > 0) groups.push({ ...group, productStyles: matchingTags });
          else if (group.name.toLowerCase().includes(this.search.toLowerCase())) groups.push(group);
        });

        groups.sort((a, b) => (a.sort > b.sort ? 1 : -1));
        return groups;
      } catch (e) {
        return [];
      }
    },
    filteredGroupsWithoutSearch() {
      try {
        let groups = this.productGroups.filter((group) => !group?.municipalGroup);
        groups.sort((a, b) => (a.sort > b.sort ? 1 : -1));
        return groups;
      } catch (e) {
        return [];
      }
    },
    marketingInfo() {
      return !this.isAdmin
        ? !this.isLicensingFlow
          ? { info: this.city?.marketingCopy, img: this.city?.marketingCopyImage?.original }
          : { info: this.city?.marketingLicensingCopy, img: this.city?.marketingLicensingCopyImage?.original }
        : { info: this.city?.marketingAdminCopy, img: this.city?.marketingAdminCopyImage?.original };
    },
    cityLogo() {
      return this.city?.letterLogo?.original ?? '';
    },
    marketingBannerVariant() {
      if (this.isLicensingFlow) {
        return 'licensing';
      }

      if (this.city.noLicensing) {
        return 'nonLicensing';
      }

      return 'default';
    },
    defaultOrReplacementCTAPromoText() {
      if (this.city?.replaceTagStoreCTAOption) {
        return this.$t('tags.titleReplacement', [this.requiredPet.name]);
      }
      return this.$t('tags.title', [this.city.safeAndHappyFund || this.$t('safeHappy')]);
    },
    defaultOrCustomCTAOptionText() {
      if (this.isAdmin) {
        return this.$t('tags.municipalTag.text.admin', [this.city.name]);
      }

      if (this.city?.replaceTagStoreCTAOption) {
        if (this.city?.customDescriptionSkipTagInLicensingFlow) {
          return this.city.customDescriptionSkipTagInLicensingFlow;
        }
        return this.$t('tags.municipalTag.text.replacementCta', [this.requiredPet.name]);
      }

      return this.$t('tags.municipalTag.text.default', [this.city.safeAndHappyFund || this.$t('safeHappy')]);
    },
  },
  watch: {
    search() {
      if (this.search) {
        this.$refs?.['group-search']?.scrollIntoView({ behavior: 'smooth' });
      }
    },
  },
  beforeRouteEnter(to, from, next) {
    const styleId = Number(to.query?.['style_id']);

    if (!styleId) next();

    next((vm) => {
      delete to.query['style_id'];
      vm.redirectToStyleId(styleId);
    });
  },
  created() {
    (async () => {
      if (!this.loading) {
        if (!this.productGroups || this.productGroups?.length === 0) {
          await this.$store.dispatch('tagProduct/fetchGroups', { cityId: this.city?.id || this.cityId });
        }
        if (this.petId && !this.order?.id) {
          await this.$store.dispatch('order/fetchOrder', { orderId: this.orderId });
        }
      }
      while (this.loading) await new Promise((resolve) => setTimeout(resolve, 1000));
    })().then(() => {
      if (this.petId && !this.order.groups?.map(({ pet }) => pet?.id)?.includes(this.petId)) {
        this.$router.push({ name: 'TagStore' });
      }
      if (!this.petId && this.requiredPet && this.requiredPet.licensing) {
        this.notifyFailure(this.$t('notifyFailure'), {
          family: 'PetRequireTag',
          dismissSecs: 10,
          dismissible: true,
          class: 'text-center',
          showInLicensing: true,
        });
      }
    });
  },
  methods: {
    orderNoTag() {
      this.$store.dispatch('order/orderNoTag', { orderId: this.order.id, petId: this.requiredPet.id }).then(() => {
        if (this.requiredPet && this.requiredPet?.licensing && !this.isAdmin) {
          this.$router.push({ name: 'ContinueToLicensing' });
        } else {
          this.$router.push({
            name: this.isAdmin && !this.getRequiredPetByPetId() ? 'AdminRedirect' : 'TagStore',
            query: { ...this.$router.currentRoute.query },
          });
        }
      });
    },
    clearSearch() {
      this.$nextTick(() => {
        this.search = '';
        this.searchKey++;
      });
    },
    scrollToHash(hash) {
      this.$nextTick(() => this.$refs?.['product-groups']?.$refs?.[hash]?.[0]?.scrollIntoView({ behavior: 'smooth' }));
    },
    redirectToStyleId(styleId) {
      if (!styleId) {
        return;
      }

      const queryParameters = { ...this.$router.currentRoute.query };

      if (!this.petId) {
        this.$router.push({
          name: 'Style',
          params: { id: styleId },
          query: queryParameters,
        });

        return;
      }

      if (this.requiredPet?.useReplacementTagInLicensingFlow && this.requiredPet?.licensing) {
        queryParameters['replacement-tag'] = 'true';
      }

      this.$router.push({
        name: 'StyleForPet',
        params: { id: styleId, petId: this.petId },
        query: queryParameters,
      });
    },
    getMarketingContent(content) {
      return content?.marketingContent;
    },
    getMarketingContentType(content) {
      return this.getMarketingContent(content)?.type;
    },
    searchTermChanged(searchTerm) {
      window.dataLayer.push({
        event: 'search_input_changed',
        search_term: searchTerm,
        is_admin: this.isAdmin,
        pet_id: this.requiredPet ? this.requiredPet.id : null,
        order_id: this.order ? this.order.id : null,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
h1 {
  margin-bottom: -2.5rem;
}
#search-empty {
  text-align: center;
  svg {
    color: $dp-gray-2;
    width: 35%;
    height: 35%;
  }
  h2 {
    margin-top: 2rem;
    color: $dp-gray-3;
  }
}
.cursor-pointer {
  cursor: pointer;
}
@media (max-width: 991px) {
  // Mobile
  .m-only {
    display: block !important;
  }
  // Desktop
  .d-only {
    display: none !important;
  }
}
@media (min-width: 991px) {
  // Mobile
  .d-only {
    display: block !important;
  }
  // Desktop
  .m-only {
    display: none !important;
  }
  .tag-options {
    margin-left: -15px;
  }
  .municipal-tag {
    flex: 0 0 50%;
  }
}
.tag-options {
  img {
    max-width: 500px;
    border-radius: 12.5px;
    border: $dp-gray-3 solid 2px;
  }
}
.municipal-tag {
  width: 100%;
  // Center the image + copy group within the row so the layout matches the
  // NASN free-tag treatment (.nasn-free-tag) at every screen size.
  justify-content: center;
  img {
    width: 160px;
    border-radius: 12.5px;
    border: $dp-gray-3 solid 2px;
  }

  // Desktop: enlarge the standard free-tag / municipal-tag images so they
  // read at a comfortable size beside the copy (mobile keeps 160px above).
  // Placed after the base rule so it wins the equal-specificity cascade.
  @media (min-width: 991px) {
    img {
      width: 280px;
    }
  }
}
.collections-list {
  padding-right: 3rem;
  a:hover h3 {
    color: $dp-navy;
  }
}
.flex-1 {
  flex: 1 !important;
}
.flex-gap-1 {
  gap: 1rem !important;
}
.nasn-intro {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 16px 16px;

  &__title-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }

  &__title {
    font-weight: 700;
    font-size: 24px;
    line-height: 1.25;
    color: $dp-navy;
    margin: 0;
  }

  &__subtitle {
    font-size: 16px;
    line-height: 1.5;
    color: $neutral-700;
    margin: 0;
  }

  &__divider {
    width: 100%;
    border: 0;
    border-top: 1px solid $dp-gray-3;
    margin: 0;
  }
}
.nasn-free-tag {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px 16px 24px;
  width: 100%;

  &__image-box {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
  }

  &__img {
    display: block;
    width: 160px;
    height: 160px;
    max-width: 160px;
    object-fit: contain;

    // Desktop: match the standard tag images — aspect-preserved, bordered,
    // and enlarged (see the shared desktop sizing in the @media block below).
    // Mobile keeps the confirmed-correct square sizing above.
    @media (min-width: 991px) {
      width: 280px;
      max-width: 280px;
      height: auto;
      object-fit: unset;
      border-radius: 12.5px;
      border: $dp-gray-3 solid 2px;
    }
  }

  &__text {
    display: flex;
    flex-direction: column;
    color: $dp-navy;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.5;
  }

  &__cta {
    font-weight: 400;
    text-decoration: underline;
  }
}
</style>
